import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Zap, Heart } from "lucide-react";
import Seo from "../components/Seo";
import api from "../lib/api";

export default function MinistryDetail() {
  const { slug } = useParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/groups/${slug}`)
      .then((r) => { if (!cancelled) setGroup(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <main id="main-content" className="bg-[#050505] text-white min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  if (error || !group) {
    return (
      <main id="main-content" className="bg-[#050505] text-white min-h-screen flex items-center justify-center px-5">
        <div className="text-center max-w-md">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">404</div>
          <h1 className="font-display font-black text-4xl mb-4">Ministry not found</h1>
          <p className="text-zinc-400 mb-8">We couldn't find the ministry you're looking for.</p>
          <Link
            to="/ministries"
            className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold hover:bg-[#4ade80] transition-colors"
            data-testid="back-to-ministries"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Ministries
          </Link>
        </div>
      </main>
    );
  }

  const heroImage = group.image_url;

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="ministry-detail-page">
      <Seo
        title={group.name}
        description={group.description || `Join the ${group.name} at The Push Center.`}
        path={`/ministries/${group.slug}`}
      />

      {/* HERO */}
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24 overflow-hidden min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          {heroImage ? (
            <img
              src={heroImage}
              alt={group.name}
              className="w-full h-full object-cover object-center opacity-90 bg-zoom"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-electric/25 via-[#0c0c0c] to-[#050505] flex items-center justify-center">
              <span className="text-[12rem] opacity-30" aria-hidden="true">{group.icon || "✨"}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/20" />
          <div className="absolute inset-0 lightning-grid opacity-20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 w-full">
          <Link
            to="/ministries"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-300 hover:text-electric mb-6 transition-colors"
            data-testid="ministry-detail-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All Ministries
          </Link>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Ministry
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            {group.name}
          </h1>
          {group.description && (
            <p className="mt-6 text-lg text-zinc-200 max-w-2xl leading-relaxed">
              {group.description}
            </p>
          )}
        </div>
      </section>

      {/* BODY */}
      <section className="max-w-5xl mx-auto px-5 lg:px-10 py-20">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6 text-zinc-300 leading-relaxed">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-electric font-bold mb-3">
                About this ministry
              </div>
              <p className="text-lg">
                {group.description || "More details about this ministry are coming soon. Reach out and we'll connect you with the team lead."}
              </p>
            </div>

            <div className="rounded-2xl border border-white/5 bg-[#0a0a0a] p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-electric/15 border border-electric/30 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 text-electric" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg mb-1">You're called to more</div>
                  <p className="text-sm text-zinc-400">
                    Every ministry is a place to grow, serve, and walk in your gifts. Whether you're new or
                    seasoned, there's a spot for you here.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA SIDEBAR */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl border border-electric/20 bg-gradient-to-br from-electric/10 via-[#0c0c0c] to-[#0c0c0c] p-7">
              <div className="text-[10px] uppercase tracking-[0.3em] text-electric font-bold mb-3">
                Step in
              </div>
              <h3 className="font-display font-black text-2xl tracking-tight mb-3 leading-tight">
                Join {group.name}
              </h3>
              <p className="text-sm text-zinc-400 mb-6">
                Let us know you're interested and we'll connect you with the team.
              </p>
              <Link
                to={`/volunteer?ministry=${encodeURIComponent(group.name)}`}
                className="w-full inline-flex items-center justify-center gap-2 bg-electric text-black px-5 py-3 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300"
                data-testid="ministry-detail-join"
              >
                <Zap className="w-4 h-4" /> Volunteer Now
              </Link>
              <Link
                to="/contact"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-white/15 text-white px-5 py-3 rounded-full font-semibold tracking-wide hover:border-electric hover:text-electric transition-all duration-300"
                data-testid="ministry-detail-contact"
              >
                Ask a Question
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
